import type { Category, Element } from "./types";
import { SHAPES } from "./shapes";

/** Color pool assigned to non-color categories. */
export const COLOR_POOL = [
  "#00d4ff",
  "#9333ea",
  "#f97316",
  "#ed3d5b",
  "#22c55e",
  "#3b82f6",
  "#eab308",
  "#a78bfa",
  "#10b981",
  "#06b6d4",
  "#ec4899",
  "#f59e0b",
] as const;

const T = (id: string, label: string, short?: string): Element =>
  short
    ? { id, display: "text", label, short }
    : { id, display: "text", label };

export const CATEGORIES: Category[] = [
  {
    id: "shapes",
    label: "SHAPES",
    needsColors: true,
    elements: SHAPES.map((s) => ({
      id: s.id,
      display: "svg",
      svg: s.svg,
      name: s.name,
    })),
    prompts: [
      {
        text: "Rank these from least YOU to most YOU today.",
        left: "LEAST YOU",
        right: "MOST YOU",
      },
      {
        text: "Order from morning vibe to night vibe.",
        left: "MORNING",
        right: "NIGHT",
      },
      {
        text: "Sequence from chill to chaotic.",
        left: "CHILL",
        right: "CHAOTIC",
      },
      {
        text: "Rank from softest to sharpest mood.",
        left: "SOFT",
        right: "SHARP",
      },
      {
        text: "Order by how loud they'd be in a group chat.",
        left: "MUTED",
        right: "GOING OFF",
      },
      {
        text: "Sequence from least to most main-character energy.",
        left: "NPC",
        right: "MAIN CHARACTER",
      },
    ],
  },
  {
    id: "numbers",
    label: "NUMBERS",
    needsColors: true,
    elements: [
      T("n3", "3"),
      T("n7", "7"),
      T("n11", "11"),
      T("n13", "13"),
      T("n21", "21"),
      T("n33", "33"),
      T("n42", "42"),
      T("n47", "47"),
      T("n64", "64"),
      T("n88", "88"),
      T("n99", "99"),
      T("n100", "100"),
      T("n111", "111"),
      T("n222", "222"),
      T("n333", "333"),
      T("n404", "404"),
      T("n420", "420"),
      T("n555", "555"),
      T("n777", "777"),
      T("n808", "808"),
      T("n888", "888"),
      T("n999", "999"),
    ],
    prompts: [
      {
        text: "Rank these numbers from least YOU to most YOU.",
        left: "LEAST YOU",
        right: "MOST YOU",
      },
      {
        text: "Order from least to most lucky.",
        left: "CURSED",
        right: "BLESSED",
      },
      {
        text: "Sequence from boring to iconic.",
        left: "BORING",
        right: "ICONIC",
      },
      { text: "Rank by quiet to loud energy.", left: "QUIET", right: "LOUD" },
      {
        text: "Order by which feels more like a vibe.",
        left: "NOT IT",
        right: "THAT'S IT",
      },
    ],
  },
  {
    id: "colors",
    label: "COLORS",
    needsColors: false,
    elements: [
      { id: "c-red", display: "swatch", color: "#ef4444", label: "RED" },
      { id: "c-orange", display: "swatch", color: "#f97316", label: "ORANGE" },
      { id: "c-yellow", display: "swatch", color: "#eab308", label: "YELLOW" },
      { id: "c-green", display: "swatch", color: "#22c55e", label: "GREEN" },
      { id: "c-blue", display: "swatch", color: "#3b82f6", label: "BLUE" },
      { id: "c-purple", display: "swatch", color: "#9333ea", label: "PURPLE" },
      { id: "c-pink", display: "swatch", color: "#ec4899", label: "PINK" },
      { id: "c-cyan", display: "swatch", color: "#06b6d4", label: "CYAN" },
      { id: "c-lime", display: "swatch", color: "#84cc16", label: "LIME" },
      { id: "c-teal", display: "swatch", color: "#14b8a6", label: "TEAL" },
      { id: "c-indigo", display: "swatch", color: "#6366f1", label: "INDIGO" },
      { id: "c-violet", display: "swatch", color: "#8b5cf6", label: "VIOLET" },
      { id: "c-amber", display: "swatch", color: "#f59e0b", label: "AMBER" },
      { id: "c-coral", display: "swatch", color: "#ed3d5b", label: "CORAL" },
      { id: "c-mint", display: "swatch", color: "#10b981", label: "MINT" },
      { id: "c-sky", display: "swatch", color: "#0ea5e9", label: "SKY" },
    ],
    prompts: [
      {
        text: "Rank these colors from least YOU to most YOU.",
        left: "LEAST YOU",
        right: "MOST YOU",
      },
      { text: "Order from coldest to warmest.", left: "COLD", right: "WARM" },
      {
        text: "Sequence from softest to loudest.",
        left: "SOFT",
        right: "LOUD",
      },
      {
        text: "Rank by which you'd wear today.",
        left: "NEVER",
        right: "OBVIOUSLY",
      },
      {
        text: "Order by morning to midnight energy.",
        left: "MORNING",
        right: "MIDNIGHT",
      },
    ],
  },
  {
    id: "months",
    label: "MONTHS",
    needsColors: true,
    elements: [
      T("jan", "JAN"),
      T("feb", "FEB"),
      T("mar", "MAR"),
      T("apr", "APR"),
      T("may", "MAY"),
      T("jun", "JUN"),
      T("jul", "JUL"),
      T("aug", "AUG"),
      T("sep", "SEP"),
      T("oct", "OCT"),
      T("nov", "NOV"),
      T("dec", "DEC"),
    ],
    prompts: [
      {
        text: "Rank these months from worst to best vibe.",
        left: "WORST",
        right: "BEST",
      },
      {
        text: "Order from least YOU to most YOU.",
        left: "LEAST YOU",
        right: "MOST YOU",
      },
      {
        text: "Sequence from coldest to warmest.",
        left: "COLDEST",
        right: "WARMEST",
      },
      {
        text: "Rank by which one you'd skip.",
        left: "SKIP IT",
        right: "KEEP IT",
      },
    ],
  },
  {
    id: "days",
    label: "DAYS",
    needsColors: true,
    elements: [
      T("mon", "MON"),
      T("tue", "TUE"),
      T("wed", "WED"),
      T("thu", "THU"),
      T("fri", "FRI"),
      T("sat", "SAT"),
      T("sun", "SUN"),
    ],
    prompts: [
      {
        text: "Rank these days from worst to best.",
        left: "WORST",
        right: "BEST",
      },
      {
        text: "Order from least to most chaotic.",
        left: "CHILL",
        right: "CHAOTIC",
      },
      {
        text: "Sequence from quietest to loudest.",
        left: "QUIET",
        right: "LOUD",
      },
      {
        text: "Rank by which one is most YOU.",
        left: "LEAST YOU",
        right: "MOST YOU",
      },
    ],
  },
  {
    id: "flavors",
    label: "FLAVORS",
    needsColors: true,
    elements: [
      T("sweet", "SWEET"),
      T("sour", "SOUR"),
      T("salty", "SALTY"),
      T("bitter", "BITTER"),
      T("umami", "UMAMI"),
      T("spicy", "SPICY"),
      T("smoky", "SMOKY"),
      T("tart", "TART"),
      T("tangy", "TANGY"),
      T("savory", "SAVORY"),
      T("minty", "MINTY"),
      T("citrus", "CITRUS"),
    ],
    prompts: [
      {
        text: "Rank flavors from least YOU to most YOU.",
        left: "LEAST YOU",
        right: "MOST YOU",
      },
      { text: "Order from softest to sharpest.", left: "SOFT", right: "SHARP" },
      {
        text: "Sequence by what you'd crave at midnight.",
        left: "NEVER",
        right: "CRAVING",
      },
      {
        text: "Rank from least to most addictive.",
        left: "WHATEVER",
        right: "ADDICTING",
      },
    ],
  },
  {
    id: "seasons",
    label: "SEASONS",
    needsColors: true,
    elements: [
      T("spring", "SPRING"),
      T("summer", "SUMMER"),
      T("fall", "FALL"),
      T("winter", "WINTER"),
      T("monsoon", "MONSOON"),
    ],
    prompts: [
      {
        text: "Rank these seasons from worst to best.",
        left: "WORST",
        right: "BEST",
      },
      {
        text: "Order from least YOU to most YOU.",
        left: "LEAST YOU",
        right: "MOST YOU",
      },
      {
        text: "Sequence by which one defines you.",
        left: "NOT ME",
        right: "ALL ME",
      },
    ],
  },
  {
    id: "cities",
    label: "CITIES",
    needsColors: true,
    elements: [
      T("nyc", "NEW YORK", "NYC"),
      T("la", "LA"),
      T("chi", "CHICAGO", "CHI"),
      T("lon", "LONDON", "LDN"),
      T("par", "PARIS"),
      T("tyo", "TOKYO"),
      T("pek", "BEIJING", "BJ"),
      T("sha", "SHANGHAI", "SHA"),
      T("bom", "MUMBAI", "BOM"),
      T("del", "DELHI"),
      T("cai", "CAIRO"),
      T("ist", "ISTANBUL", "IST"),
      T("mow", "MOSCOW", "MSK"),
      T("ber", "BERLIN", "BER"),
      T("rom", "ROME"),
      T("mad", "MADRID"),
      T("syd", "SYDNEY", "SYD"),
      T("tor", "TORONTO", "TOR"),
      T("mex", "MEXICO", "MEX"),
      T("rio", "RIO"),
      T("sao", "SÃO PAULO", "SP"),
      T("dxb", "DUBAI"),
      T("bkk", "BANGKOK", "BKK"),
      T("hkg", "HONG KONG", "HK"),
      T("sin", "SINGAPORE", "SG"),
    ],
    prompts: [
      {
        text: "Rank these cities from least to most YOU.",
        left: "LEAST YOU",
        right: "MOST YOU",
      },
      {
        text: "Order by where you'd wake up tomorrow.",
        left: "NEVER",
        right: "TONIGHT",
      },
      {
        text: "Sequence from least to most chaotic energy.",
        left: "CHILL",
        right: "CHAOTIC",
      },
      {
        text: "Rank by which one feels like home.",
        left: "STRANGER",
        right: "HOME",
      },
      {
        text: "Order from softest to loudest vibe.",
        left: "SOFT",
        right: "LOUD",
      },
    ],
  },
  {
    id: "music",
    label: "MUSIC",
    needsColors: true,
    elements: [
      T("pop", "POP"),
      T("hiphop", "HIP-HOP"),
      T("rock", "ROCK"),
      T("indie", "INDIE"),
      T("edm", "EDM"),
      T("rnb", "R&B"),
      T("jazz", "JAZZ"),
      T("country", "COUNTRY", "CTRY"),
      T("kpop", "K-POP"),
      T("latin", "LATIN"),
      T("metal", "METAL"),
      T("punk", "PUNK"),
      T("house", "HOUSE"),
      T("techno", "TECHNO"),
      T("reggae", "REGGAE", "REG"),
      T("soul", "SOUL"),
      T("funk", "FUNK"),
      T("lofi", "LO-FI"),
      T("synth", "SYNTHWAVE", "SYNTH"),
      T("trap", "TRAP"),
      T("drill", "DRILL"),
      T("classical", "CLASSICAL", "CLASS"),
    ],
    prompts: [
      {
        text: "Rank these genres from least to most YOU.",
        left: "LEAST YOU",
        right: "MOST YOU",
      },
      { text: "Order from softest to hardest.", left: "SOFT", right: "HARD" },
      {
        text: "Sequence by what's on your phone right now.",
        left: "NEVER",
        right: "ON REPEAT",
      },
      {
        text: "Rank by what you'd play at a party.",
        left: "NEVER",
        right: "TURN UP",
      },
    ],
  },
];
